import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useWorkspaces } from "@/hooks/use-workspaces"
import { supabase } from "@/integrations/supabase/client"
import { useToast } from "@/hooks/use-toast"

interface BoardCreationFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export function BoardCreationForm({ onSuccess, onCancel }: BoardCreationFormProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const [boardName, setBoardName] = useState("")
  const [selectedWorkspace, setSelectedWorkspace] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const { data: workspaces = [] } = useWorkspaces()

  const handleCreateBoard = async () => {
    if (!boardName.trim() || !selectedWorkspace) return

    setIsLoading(true)
    try {
      const { error } = await supabase
        .from("boards")
        .insert([{ name: boardName, workspace_id: selectedWorkspace }])

      if (error) throw error

      toast({
        title: t("common.boards.created"),
        description: t("common.boards.createdDescription"),
      })
      onSuccess()
    } catch (error) {
      console.error("Error creating board:", error)
      toast({
        title: t("common.error"),
        description: t("common.boards.errorCreating"),
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="space-y-4 py-4">
      <div className="space-y-2">
        <Label htmlFor="workspace">{t("common.workspaces.select")}</Label>
        <Select value={selectedWorkspace} onValueChange={setSelectedWorkspace}>
          <SelectTrigger>
            <SelectValue placeholder={t("common.workspaces.selectPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            {workspaces.map((workspace) => (
              <SelectItem key={workspace.id} value={workspace.id}>
                {workspace.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label htmlFor="name">{t("common.boards.name")}</Label>
        <Input
          id="name"
          value={boardName}
          onChange={(e) => setBoardName(e.target.value)}
          placeholder={t("common.boards.namePlaceholder")}
        />
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          onClick={handleCreateBoard}
          disabled={isLoading || !selectedWorkspace || !boardName.trim()}
        >
          {t("common.create")}
        </Button>
      </div>
    </div>
  )
}